/**
 * @file Submission UI — A3DetailsReadonlySection.
 * A3 on Dataset Overview — same UI as Form A3 (A3Section + summaries).
 */

import React, { useMemo } from "react";
import type { FormDataResponse, FormSubSection } from "../../interfaces/formInterfaces";
import "../../styles/formBase.css";
import "../../pages/Dashboard/Contributor/Stages/A3/FormA3.css";
import "./LifecycleStageDetailsReadonly.css";
import A3Section from "../../pages/Dashboard/Contributor/Stages/A3/components/A3Section";
import A3EnergySummary from "../../pages/Dashboard/Contributor/Stages/A3/components/A3EnergySummary";
import A3CarbonSummary from "../../pages/Dashboard/Contributor/Stages/A3/components/A3CarbonSummary";
import { useA3Prefill } from "../../pages/Dashboard/Contributor/Stages/A3/hooks/useA3Prefill";
import {
  alignA3FuelsPerProcessRowCount,
  buildA3FuelLoadLayout,
} from "../../pages/Dashboard/Contributor/Stages/A3/utils/a3ProcessFuelLayout";
import { readBackgroundTechnologyForA3 } from "../../utils/a3ManufacturingSpecificityField";
import DatasetOverviewFormShell from "./datasetOverview/DatasetOverviewFormShell";
import DatasetOverviewReadonlyBlock from "./datasetOverview/DatasetOverviewReadonlyBlock";
import {
  perFinishedCarbonUnitLabel,
  perFinishedEnergyUnitLabel,
  readBackgroundFinishedDeclaredQty,
  resolveBackgroundFinishedMaterialUnitSymbol,
  sumA3CarbonKgForSummary,
  sumA3EnergyMjForSummary,
} from "../../pages/Dashboard/Contributor/Stages/A1-A3/a1a3SummaryFromSubmission";
import type { AnyObj, ReferenceLabels } from "./readonlyFormDisplay";

const noop = () => {};

/** Type definition — A3Details Readonly Section Props. */
export type A3DetailsReadonlySectionProps = {
  submission: AnyObj;
};

const A3DetailsReadonlySection: React.FC<A3DetailsReadonlySectionProps> = ({
  submission,
}) => {
  const formSchema: AnyObj[] = Array.isArray(submission.formSchema)
    ? submission.formSchema
    : [];

  const stage = formSchema.find(
    (s) => s.code === "RAW_MATERIAL_MANUFACTURING_A3"
  );
  const sections: FormSubSection[] =
    stage && Array.isArray(stage.formSubSections)
      ? (stage.formSubSections as FormSubSection[])
      : [];

  const formData = stage as FormDataResponse | undefined;

  const backgroundRawMaterialOptions = useMemo(() => {
    const rows = (submission.backgroundRawMats ??
      submission.backgroundRawMaterials ??
      []) as AnyObj[];
    return rows
      .map((r, i) => ({
        id: Number(r.rawMaterialId ?? r.raw_material_id ?? i + 1),
        name: String(r.rawMaterialName ?? r.raw_material_name ?? ""),
      }))
      .filter((r) => r.name.trim() !== "");
  }, [submission]);

  const { values, energyTotal } = useA3Prefill(
    sections,
    submission,
    backgroundRawMaterialOptions,
    formData ?? null
  );

  const processRows = (submission.A3_Process ?? []) as AnyObj[];
  const a3ProcessDetailsRowCount = Math.max(1, processRows.length);

  const fuelsPerProcess = useMemo(() => {
    const { counts } = buildA3FuelLoadLayout(
      submission.A3_Process as unknown[] | undefined,
      submission.A3_Fuel as unknown[] | undefined
    );
    return alignA3FuelsPerProcessRowCount(counts, a3ProcessDetailsRowCount);
  }, [submission, a3ProcessDetailsRowCount]);

  const finishedMaterialId = useMemo(() => {
    const bg = submission.background as AnyObj | undefined;
    const id = bg?.materialId;
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [submission]);

  const manufacturingSpecificityFromBackground = useMemo(() => {
    const fromSubmission = readBackgroundTechnologyForA3(
      submission.background as AnyObj | undefined
    );
    if (fromSubmission.display || fromSubmission.id) {
      return {
        id: fromSubmission.id,
        display: fromSubmission.display,
      };
    }
    return null;
  }, [submission]);

  const referenceLabels = useMemo(
    () => submission.referenceLabels as ReferenceLabels | undefined,
    [submission.referenceLabels]
  );

  const energyMj = useMemo(
    () => sumA3EnergyMjForSummary(submission),
    [submission]
  );

  const finishedQty = useMemo(
    () => readBackgroundFinishedDeclaredQty(submission),
    [submission]
  );

  const finishedMaterialUnitSymbol = useMemo(
    () => resolveBackgroundFinishedMaterialUnitSymbol(submission),
    [submission]
  );

  const perFinishedEnergyUnit = useMemo(
    () => perFinishedEnergyUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const perFinishedCarbonUnit = useMemo(
    () => perFinishedCarbonUnitLabel(finishedMaterialUnitSymbol),
    [finishedMaterialUnitSymbol]
  );

  const energyPerFinished = useMemo(() => {
    if (finishedQty == null || energyMj <= 0) return null;
    return Math.round((energyMj / finishedQty) * 1000) / 1000;
  }, [energyMj, finishedQty]);

  const carbonKg = useMemo(
    () => sumA3CarbonKgForSummary(submission),
    [submission]
  );

  const carbonPerFinished = useMemo(() => {
    if (finishedQty == null || carbonKg <= 0) return null;
    return Math.round((carbonKg / finishedQty) * 1000) / 1000;
  }, [carbonKg, finishedQty]);

  if (sections.length === 0) {
    return (
      <DatasetOverviewFormShell
        variant="lifecycle-a3"
        bare
        emptyMessage="No A3 form sections configured."
      />
    );
  }

  return (
    <DatasetOverviewFormShell variant="lifecycle-a3" bare>
      {sections.map((section) => {
        const code = section.code || "";

        return (
          <DatasetOverviewReadonlyBlock
            key={code}
            title={section.name ?? code}
          >
            <A3Section
              section={section}
              values={values}
              materialId={finishedMaterialId}
              manufacturingSpecificityFromBackground={
                manufacturingSpecificityFromBackground
              }
              submissionBackground={
                (submission.background as AnyObj) ?? null
              }
              onChange={noop}
              backgroundRawMaterialOptions={backgroundRawMaterialOptions}
              processDetailsRowCount={a3ProcessDetailsRowCount}
              fuelsPerProcess={fuelsPerProcess}
              formData={formData ?? null}
              readonly
              referenceLabels={referenceLabels}
            />
            {code === "A3_EMBODIED_ENERGY_DETAILS" && (
              <A3EnergySummary
                energyMj={energyMj}
                perDeclaredFinishedMj={energyPerFinished}
                perFinishedUnitLabel={perFinishedEnergyUnit}
              />
            )}
            {code === "A3_EMBODIED_CARBON_DETAILS" && (
              <A3CarbonSummary
                totalEmbodiedCarbonKg={carbonKg}
                perDeclaredFinishedKg={carbonPerFinished}
                perFinishedUnitLabel={perFinishedCarbonUnit}
              />
            )}
          </DatasetOverviewReadonlyBlock>
        );
      })}
    </DatasetOverviewFormShell>
  );
};

/** React component — A3Details Readonly Section. */
export default A3DetailsReadonlySection;
